import { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  Plus,
  Search,
  Filter,
  Download,
  List,
  LayoutGrid,
  AlertTriangle,
  Clock,
  X,
  Send,
  MoreHorizontal,
  ExternalLink,
  Flag,
  Calendar,
  User,
  MapPin,
  Trash2,
} from "lucide-react";
import styles from "./Issues.module.css";
import {
  listIssues,
  listComments,
  createComment,
  deleteComment,
  createIssue,
  updateIssue,
  deleteIssue,
  fetchMyUrns,
  fetchUserProfile,
  listAttachments,
  uploadAttachment,
  deleteAttachment,
  downloadAttachmentBlob,
  listAssignees,
  replaceAssignees,
  listAudit,
} from "../../services/issuesApi";

const statusConfig = {
  open: { label: "Open", className: styles.statusOpen },
  in_progress: { label: "In Progress", className: styles.statusInProgress },
  resolved: { label: "Resolved", className: styles.statusResolved },
  closed: { label: "Closed", className: styles.statusClosed },
};

const priorityConfig = {
  critical: {
    label: "Critical",
    className: styles.priorityCritical,
    icon: <AlertTriangle size={12} />,
  },
  high: {
    label: "High",
    className: styles.priorityHigh,
    icon: <Flag size={12} />,
  },
  normal: {
    label: "Normal",
    className: styles.priorityNormal,
    icon: <Flag size={12} />,
  },
  low: {
    label: "Low",
    className: styles.priorityLow,
    icon: <Flag size={12} />,
  },
};

const typeConfig = {
  clash: { label: "Clash", className: styles.typeClash },
  design: { label: "Design", className: styles.typeDesign },
  safety: { label: "Safety", className: styles.typeSafety },
  quality: { label: "Quality", className: styles.typeQuality },
  rfi: { label: "RFI", className: styles.typeRfi },
};

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return "—";
  }
}

function normalizeStatus(s) {
  if (!s) return "open";
  const l = String(s).toLowerCase();
  if (l.includes("closed")) return "closed";
  if (l.includes("resolved") || l.includes("completed")) return "resolved";
  if (l.includes("progress")) return "in_progress";
  return "open";
}

function uiTypeFromSubtype(subtype, issueType) {
  if (issueType === "rfis") return "rfi";
  const s = (subtype || "").toLowerCase();
  if (s.includes("quality")) return "quality";
  if (s.includes("safety")) return "safety";
  if (s.includes("design")) return "design";
  if (s.includes("clash")) return "clash";
  return "quality";
}

function mapApiRowToIssue(row) {
  const p = row.payload || {};
  const client = p.clientIssue || {};
  const subtype = client.subtype || p.subtype || "";
  return {
    serverId: row.id,
    id: `ISS-${row.id}`,
    title: row.label,
    description: row.description || "",
    status: normalizeStatus(row.status),
    priority: p.priority || client.priority || "normal",
    type: uiTypeFromSubtype(subtype, row.issue_type),
    assignee: "—",
    creator: "—",
    location: p.location || client.location || "—",
    dueDate: p.dueDate || client.dueDate || formatDate(row.created_at),
    createdDate: formatDate(row.created_at),
    comments: [],
    raw: row,
  };
}

const getInitials = (name) => {
  if (!name || name === "—") return "?";
  return name
    .split(" ")
    .map((w) => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
};

// const modalOverlay = {
//   position: "fixed",
//   inset: 0,
//   background: "rgba(0,0,0,0.45)",
//   display: "flex",
//   alignItems: "center",
//   justifyContent: "center",
//   zIndex: 1000,
// };
// const modalBox = {
//   background: "#fff",
//   borderRadius: 8,
//   padding: 24,
//   minWidth: 360,
//   maxWidth: "90vw",
//   maxHeight: "85vh",
//   overflow: "auto",
// };
const field = {
  marginBottom: 12,
  display: "flex",
  flexDirection: "column",
  gap: 4,
};

const modalOverlay = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.45)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 1000,
  padding: 12,
};
const modalBox = {
  background: "#fff",
  borderRadius: 8,
  padding: 24,
  width: "min(420px, 100%)",
  maxHeight: "85vh",
  overflow: "auto",
};
const inputStyle = {
  padding: 8,
  borderRadius: 4,
  border: "1px solid #ccc",
  fontSize: 16,
};
// const inputStyle = { padding: 8, borderRadius: 4, border: "1px solid #ccc" };

const Issues = () => {
  const [issues, setIssues] = useState([]);
  const [loadError, setLoadError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedIssue, setSelectedIssue] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterPriority, setFilterPriority] = useState("all");
  const [selectedIds, setSelectedIds] = useState(new Set());

  const [showNewModal, setShowNewModal] = useState(false);
  const [urnOptions, setUrnOptions] = useState([]);
  const [newForm, setNewForm] = useState({
    user_urn_id: "",
    label: "",
    description: "",
    status: "open",
    issue_type: "issues",
    subtype: "quality_issue",
  });
  const [currentUserId, setCurrentUserId] = useState(null);

  const [attachments, setAttachments] = useState([]);
  const [auditRows, setAuditRows] = useState([]);
  const [assigneeNames, setAssigneeNames] = useState([]);
  const [assigneeIdsInput, setAssigneeIdsInput] = useState("");
  const [commentText, setCommentText] = useState("");
  const [actionError, setActionError] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);

  const [editTitle, setEditTitle] = useState("");
  const [editStatus, setEditStatus] = useState("open");
  const [editDescription, setEditDescription] = useState("");

  const refreshIssues = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const rows = await listIssues({ limit: 300 });
      setIssues(rows.map(mapApiRowToIssue));
    } catch (e) {
      setLoadError(e?.response?.data?.detail || e.message || "Failed to load");
      setIssues([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshIssues();
  }, [refreshIssues]);

  useEffect(() => {
    let c = false;
    fetchUserProfile()
      .then((p) => {
        if (!c && p?.id != null) setCurrentUserId(p.id);
      })
      .catch(() => {});
    return () => {
      c = true;
    };
  }, []);

  const selectedId = selectedIssue?.serverId;
  useEffect(() => {
    if (!selectedId) return;
    let cancelled = false;
    listComments(selectedId)
      .then((list) => {
        if (cancelled) return;
        const mapped = (list || []).map((c) => ({
          id: c.id,
          author_id: c.author_id,
          author: `User #${c.author_id}`,
          text: c.body,
          time: formatDate(c.created_at),
        }));
        setSelectedIssue((s) =>
          s && s.serverId === selectedId ? { ...s, comments: mapped } : s,
        );
      })
      .catch(() => {
        if (!cancelled) {
          setSelectedIssue((s) =>
            s && s.serverId === selectedId ? { ...s, comments: [] } : s,
          );
        }
      });
    return () => {
      cancelled = true;
    };
  }, [selectedId]);

  useEffect(() => {
    if (!selectedId) {
      setAttachments([]);
      setAuditRows([]);
      setAssigneeNames([]);
      setAssigneeIdsInput("");
      setCommentText("");
      setEditTitle("");
      setEditStatus("open");
      setEditDescription("");
      return;
    }
    let c = false;
    (async () => {
      try {
        const [atts, assigns, aud] = await Promise.all([
          listAttachments(selectedId),
          listAssignees(selectedId),
          listAudit(selectedId, { limit: 50 }),
        ]);
        if (c) return;
        setAttachments(atts || []);
        setAssigneeNames(assigns || []);
        setAssigneeIdsInput((assigns || []).map((a) => a.user_id).join(", "));
        setAuditRows(aud || []);
      } catch {
        if (!c) {
          setAttachments([]);
          setAssigneeNames([]);
          setAuditRows([]);
        }
      }
    })();
    return () => {
      c = true;
    };
  }, [selectedId]);

  useEffect(() => {
    if (selectedIssue) {
      setEditTitle(selectedIssue.title);
      setEditStatus(selectedIssue.status);
      setEditDescription(selectedIssue.description || "");
    }
  }, [selectedIssue?.serverId]);

  const openNewModal = async () => {
    setActionError(null);
    setShowNewModal(true);
    try {
      const urns = await fetchMyUrns();
      setUrnOptions(urns || []);
      if (urns?.length && !newForm.user_urn_id) {
        setNewForm((f) => ({ ...f, user_urn_id: String(urns[0].id) }));
      }
    } catch (e) {
      setActionError(e?.response?.data?.detail || "Could not load documents");
    }
  };

  const submitNewIssue = async () => {
    setActionError(null);
    const uid = parseInt(newForm.user_urn_id, 10);
    if (!Number.isFinite(uid) || !newForm.label.trim()) {
      setActionError("Choose a document and enter a title.");
      return;
    }
    setSaving(true);
    try {
      await createIssue({
        user_urn_id: uid,
        issue_type: newForm.issue_type,
        status: newForm.status,
        label: newForm.label.trim(),
        description: newForm.description.trim() || null,
        payload: {
          clientIssue: { subtype: newForm.subtype },
          priority: "normal",
        },
      });
      setShowNewModal(false);
      setNewForm({
        user_urn_id: "",
        label: "",
        description: "",
        status: "open",
        issue_type: "issues",
        subtype: "quality_issue",
      });
      await refreshIssues();
    } catch (e) {
      setActionError(e?.response?.data?.detail || e.message || "Create failed");
    } finally {
      setSaving(false);
    }
  };

  const saveIssueEdits = async () => {
    if (!selectedId) return;
    setSaving(true);
    setActionError(null);
    try {
      await updateIssue(selectedId, {
        label: editTitle.trim(),
        status: editStatus,
        description: editDescription.trim() || null,
      });
      await refreshIssues();
      setSelectedIssue((s) =>
        s && s.serverId === selectedId
          ? {
              ...s,
              title: editTitle.trim(),
              status: normalizeStatus(editStatus),
              description: editDescription.trim(),
            }
          : s,
      );
    } catch (e) {
      setActionError(e?.response?.data?.detail || e.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const removeSelectedIssue = async () => {
    if (!selectedId) return;
    if (!window.confirm("Delete this issue permanently?")) return;
    setSaving(true);
    setActionError(null);
    try {
      await deleteIssue(selectedId);
      setSelectedIssue(null);
      await refreshIssues();
    } catch (e) {
      setActionError(e?.response?.data?.detail || e.message || "Delete failed");
    } finally {
      setSaving(false);
    }
  };

  const sendComment = async () => {
    if (!selectedId || !commentText.trim()) return;
    setSaving(true);
    setActionError(null);
    try {
      await createComment(selectedId, { body: commentText.trim() });
      setCommentText("");
      const list = await listComments(selectedId);
      const mapped = (list || []).map((c) => ({
        id: c.id,
        author_id: c.author_id,
        author: `User #${c.author_id}`,
        text: c.body,
        time: formatDate(c.created_at),
      }));
      setSelectedIssue((s) =>
        s && s.serverId === selectedId ? { ...s, comments: mapped } : s,
      );
    } catch (e) {
      setActionError(
        e?.response?.data?.detail || e.message || "Comment failed",
      );
    } finally {
      setSaving(false);
    }
  };

  const removeComment = async (commentId) => {
    if (!selectedId) return;
    setSaving(true);
    setActionError(null);
    try {
      await deleteComment(selectedId, commentId);
      const list = await listComments(selectedId);
      const mapped = (list || []).map((c) => ({
        id: c.id,
        author_id: c.author_id,
        author: `User #${c.author_id}`,
        text: c.body,
        time: formatDate(c.created_at),
      }));
      setSelectedIssue((s) =>
        s && s.serverId === selectedId ? { ...s, comments: mapped } : s,
      );
    } catch (e) {
      setActionError(e?.response?.data?.detail || e.message || "Delete failed");
    } finally {
      setSaving(false);
    }
  };

  const onPickAttachment = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !selectedId) return;
    setSaving(true);
    setActionError(null);
    try {
      await uploadAttachment(selectedId, file);
      setAttachments(await listAttachments(selectedId));
    } catch (err) {
      setActionError(
        err?.response?.data?.detail || err.message || "Upload failed",
      );
    } finally {
      setSaving(false);
    }
  };

  const downloadAtt = async (att) => {
    if (!selectedId) return;
    try {
      const res = await downloadAttachmentBlob(selectedId, att.id);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = att.filename || "download";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setActionError(e?.message || "Download failed");
    }
  };

  const removeAtt = async (attId) => {
    if (!selectedId) return;
    setSaving(true);
    try {
      await deleteAttachment(selectedId, attId);
      setAttachments(await listAttachments(selectedId));
    } catch (e) {
      setActionError(e?.response?.data?.detail || e.message || "Delete failed");
    } finally {
      setSaving(false);
    }
  };

  const saveAssignees = async () => {
    if (!selectedId) return;
    const ids = assigneeIdsInput
      .split(/[\s,]+/)
      .map((s) => parseInt(s.trim(), 10))
      .filter((n) => Number.isFinite(n));
    setSaving(true);
    setActionError(null);
    try {
      const list = await replaceAssignees(selectedId, ids);
      setAssigneeNames(list || []);
    } catch (e) {
      setActionError(
        e?.response?.data?.detail || e.message || "Assignees failed",
      );
    } finally {
      setSaving(false);
    }
  };

  const refreshAudit = async () => {
    if (!selectedId) return;
    try {
      setAuditRows(await listAudit(selectedId, { limit: 100 }));
    } catch {
      setAuditRows([]);
    }
  };

  const filtered = issues.filter((issue) => {
    const matchesSearch =
      issue.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      issue.id.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus =
      filterStatus === "all" || issue.status === filterStatus;
    const matchesPriority =
      filterPriority === "all" || issue.priority === filterPriority;
    return matchesSearch && matchesStatus && matchesPriority;
  });

  const isOverdue = (date) => {
    if (!date || date === "—") return false;
    try {
      return new Date(date) < new Date();
    } catch {
      return false;
    }
  };

  const toggleSelect = (id, e) => {
    e.stopPropagation();
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const typeBadge = (issue) => typeConfig[issue.type] || typeConfig.quality;
  const statusBadge = (issue) =>
    statusConfig[issue.status] || statusConfig.open;
  const priorityBadge = (issue) =>
    priorityConfig[issue.priority] || priorityConfig.normal;

  return (
    <div className={styles.container}>
      {showNewModal && (
        <div style={modalOverlay} role="presentation">
          <div style={modalBox}>
            <h3 style={{ marginTop: 0 }}>New issue</h3>
            {actionError && (
              <p style={{ color: "#c62828", fontSize: 14 }}>{actionError}</p>
            )}
            <div style={field}>
              <label>Document</label>
              <select
                style={inputStyle}
                value={newForm.user_urn_id}
                onChange={(e) =>
                  setNewForm((f) => ({ ...f, user_urn_id: e.target.value }))
                }
              >
                <option value="">Select…</option>
                {urnOptions.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.original_filename || u.description || u.urn}
                  </option>
                ))}
              </select>
            </div>
            <div style={field}>
              <label>Title</label>
              <input
                style={inputStyle}
                value={newForm.label}
                onChange={(e) =>
                  setNewForm((f) => ({ ...f, label: e.target.value }))
                }
              />
            </div>
            <div style={field}>
              <label>Description</label>
              <textarea
                style={{ ...inputStyle, minHeight: 80 }}
                value={newForm.description}
                onChange={(e) =>
                  setNewForm((f) => ({ ...f, description: e.target.value }))
                }
              />
            </div>
            <div style={field}>
              <label>Status</label>
              <select
                style={inputStyle}
                value={newForm.status}
                onChange={(e) =>
                  setNewForm((f) => ({ ...f, status: e.target.value }))
                }
              >
                <option value="open">open</option>
                <option value="in_progress">in_progress</option>
                <option value="resolved">resolved</option>
                <option value="closed">closed</option>
              </select>
            </div>
            <div style={field}>
              <label>Type</label>
              <select
                style={inputStyle}
                value={newForm.issue_type}
                onChange={(e) =>
                  setNewForm((f) => ({ ...f, issue_type: e.target.value }))
                }
              >
                <option value="issues">issues</option>
                <option value="rfis">rfis</option>
              </select>
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
              <button
                type="button"
                className={styles.btnPrimary}
                disabled={saving}
                onClick={submitNewIssue}
              >
                Create
              </button>
              <button
                type="button"
                className={styles.btnSecondary}
                onClick={() => {
                  setShowNewModal(false);
                  setActionError(null);
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <div className={styles.topBar}>
        <div className={styles.topBarLeft}>
          <Link to="/dashboard" className={styles.backLink}>
            <ArrowLeft size={16} /> Dashboard
          </Link>
          <h1 className={styles.pageTitle}>
            Issues <span className={styles.issueCount}>{filtered.length}</span>
          </h1>
        </div>
        <div className={styles.topBarRight}>
          <button type="button" className={styles.btnSecondary}>
            <Download size={14} /> Export
          </button>
          <button
            type="button"
            className={styles.btnPrimary}
            onClick={openNewModal}
          >
            <Plus size={14} /> New Issue
          </button>
        </div>
      </div>

      {loadError && (
        <div style={{ padding: "12px 24px", color: "#c62828" }}>
          {loadError}
        </div>
      )}
      {loading && <div style={{ padding: "12px 24px" }}>Loading issues…</div>}

      <div className={styles.toolbar}>
        <div className={styles.searchBox}>
          <Search size={14} />
          <input
            className={styles.searchInput}
            placeholder="Search issues..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        <button
          type="button"
          className={`${styles.filterBtn} ${filterStatus !== "all" ? styles.filterBtnActive : ""}`}
          onClick={() =>
            setFilterStatus((prev) =>
              prev === "all"
                ? "open"
                : prev === "open"
                  ? "in_progress"
                  : prev === "in_progress"
                    ? "resolved"
                    : prev === "resolved"
                      ? "closed"
                      : "all",
            )
          }
        >
          <Filter size={14} /> Status{" "}
          {filterStatus !== "all" &&
            `: ${(statusConfig[filterStatus] || statusConfig.open).label}`}
        </button>

        <button
          type="button"
          className={`${styles.filterBtn} ${filterPriority !== "all" ? styles.filterBtnActive : ""}`}
          onClick={() =>
            setFilterPriority((prev) =>
              prev === "all"
                ? "critical"
                : prev === "critical"
                  ? "high"
                  : prev === "high"
                    ? "normal"
                    : prev === "normal"
                      ? "low"
                      : "all",
            )
          }
        >
          <Flag size={14} /> Priority{" "}
          {filterPriority !== "all" &&
            `: ${(priorityConfig[filterPriority] || priorityConfig.normal).label}`}
        </button>

        <div className={styles.separator} />

        <div className={styles.viewToggle}>
          <button
            type="button"
            className={`${styles.viewBtn} ${styles.viewBtnActive}`}
          >
            <List size={14} />
          </button>
          <button type="button" className={styles.viewBtn}>
            <LayoutGrid size={14} />
          </button>
        </div>
      </div>

      <div className={styles.mainContent}>
        <div className={styles.tableContainer}>
          <table className={styles.issueTable}>
            <thead>
              <tr>
                <th style={{ width: 40 }}></th>
                <th>ID</th>
                <th>Title</th>
                <th>Type</th>
                <th>Status</th>
                <th>Priority</th>
                <th>Assignee</th>
                <th>Location</th>
                <th>Due Date</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((issue) => {
                const tc = typeBadge(issue);
                const sc = statusBadge(issue);
                const pc = priorityBadge(issue);
                return (
                  <tr
                    key={issue.serverId}
                    className={`${styles.issueRow} ${selectedIssue?.serverId === issue.serverId ? styles.issueRowSelected : ""}`}
                    onClick={() => setSelectedIssue({ ...issue, comments: [] })}
                  >
                    <td>
                      <input
                        type="checkbox"
                        className={styles.checkbox}
                        checked={selectedIds.has(issue.id)}
                        onClick={(e) => toggleSelect(issue.id, e)}
                        readOnly
                      />
                    </td>
                    <td>
                      <span className={styles.issueId}>{issue.id}</span>
                    </td>
                    <td>
                      <span className={styles.issueTitle}>{issue.title}</span>
                    </td>
                    <td>
                      <span className={`${styles.typeBadge} ${tc.className}`}>
                        {tc.label}
                      </span>
                    </td>
                    <td>
                      <span className={`${styles.statusBadge} ${sc.className}`}>
                        {sc.label}
                      </span>
                    </td>
                    <td>
                      <span
                        className={`${styles.priorityBadge} ${pc.className}`}
                      >
                        {pc.icon} {pc.label}
                      </span>
                    </td>
                    <td>
                      <span className={styles.assignee}>
                        <span className={styles.assigneeAvatar}>
                          {getInitials(issue.assignee)}
                        </span>
                        {issue.assignee}
                      </span>
                    </td>
                    <td>{issue.location}</td>
                    <td>
                      <span
                        className={`${styles.dueDate} ${isOverdue(issue.dueDate) ? styles.dueDateOverdue : ""}`}
                      >
                        {issue.dueDate}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {!loading && filtered.length === 0 && (
            <div className={styles.emptyState}>
              <AlertTriangle size={40} />
              <p>No issues found matching your criteria.</p>
            </div>
          )}
        </div>

        {selectedIssue && (
          <div className={styles.detailPanel}>
            <div className={styles.detailHeader}>
              <span className={styles.detailId}>{selectedIssue.id}</span>
              <div className={styles.detailActions}>
                <button type="button" className={styles.detailActionBtn}>
                  <ExternalLink size={14} />
                </button>
                <button type="button" className={styles.detailActionBtn}>
                  <MoreHorizontal size={14} />
                </button>
                <button
                  type="button"
                  className={styles.detailActionBtn}
                  onClick={() => setSelectedIssue(null)}
                >
                  <X size={14} />
                </button>
              </div>
            </div>
            <div className={styles.detailBody}>
              {actionError && (
                <p style={{ color: "#c62828", fontSize: 13 }}>{actionError}</p>
              )}

              <h3 style={{ margin: "8px 0" }}>Edit</h3>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <input
                  className={styles.searchInput}
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                />
                <select
                  className={styles.searchInput}
                  value={editStatus}
                  onChange={(e) => setEditStatus(e.target.value)}
                >
                  <option value="open">open</option>
                  <option value="in_progress">in_progress</option>
                  <option value="resolved">resolved</option>
                  <option value="closed">closed</option>
                </select>
                <textarea
                  className={styles.searchInput}
                  style={{ minHeight: 72 }}
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                />
                <div style={{ display: "flex", gap: 8 }}>
                  <button
                    type="button"
                    className={styles.btnPrimary}
                    disabled={saving}
                    onClick={saveIssueEdits}
                  >
                    Save changes
                  </button>
                  <button
                    type="button"
                    className={styles.btnSecondary}
                    disabled={saving}
                    onClick={removeSelectedIssue}
                  >
                    <Trash2 size={14} /> Delete
                  </button>
                </div>
              </div>

              <div className={styles.detailDivider} />

              <h2 className={styles.detailTitle}>{selectedIssue.title}</h2>
              <p className={styles.detailDescription}>
                {selectedIssue.description}
              </p>

              <div className={styles.detailDivider} />

              <div className={styles.detailMeta}>
                <span className={styles.detailLabel}>Status</span>
                <span className={styles.detailValue}>
                  <span
                    className={`${styles.statusBadge} ${statusBadge(selectedIssue).className}`}
                  >
                    {statusBadge(selectedIssue).label}
                  </span>
                </span>

                <span className={styles.detailLabel}>Priority</span>
                <span className={styles.detailValue}>
                  <span
                    className={`${styles.priorityBadge} ${priorityBadge(selectedIssue).className}`}
                  >
                    {priorityBadge(selectedIssue).icon}{" "}
                    {priorityBadge(selectedIssue).label}
                  </span>
                </span>

                <span className={styles.detailLabel}>Type</span>
                <span className={styles.detailValue}>
                  <span
                    className={`${styles.typeBadge} ${typeBadge(selectedIssue).className}`}
                  >
                    {typeBadge(selectedIssue).label}
                  </span>
                </span>

                <span className={styles.detailLabel}>
                  <User size={13} /> Assignee
                </span>
                <span className={styles.detailValue}>
                  {assigneeNames.length
                    ? assigneeNames.map((a) => a.name || a.email).join(", ")
                    : selectedIssue.assignee}
                </span>

                <span className={styles.detailLabel}>
                  <User size={13} /> Creator
                </span>
                <span className={styles.detailValue}>
                  {selectedIssue.creator}
                </span>

                <span className={styles.detailLabel}>
                  <MapPin size={13} /> Location
                </span>
                <span className={styles.detailValue}>
                  {selectedIssue.location}
                </span>

                <span className={styles.detailLabel}>
                  <Calendar size={13} /> Due Date
                </span>
                <span className={styles.detailValue}>
                  <span
                    className={
                      isOverdue(selectedIssue.dueDate)
                        ? styles.dueDateOverdue
                        : ""
                    }
                  >
                    {selectedIssue.dueDate}
                  </span>
                </span>

                <span className={styles.detailLabel}>
                  <Clock size={13} /> Created
                </span>
                <span className={styles.detailValue}>
                  {selectedIssue.createdDate}
                </span>
              </div>

              <div className={styles.detailDivider} />
              <h4 className={styles.detailSectionTitle}>
                Assignees (user IDs)
              </h4>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <input
                  className={styles.searchInput}
                  style={{ flex: 1, minWidth: 200 }}
                  placeholder="e.g. 1, 2, 3"
                  value={assigneeIdsInput}
                  onChange={(e) => setAssigneeIdsInput(e.target.value)}
                />
                <button
                  type="button"
                  className={styles.btnSecondary}
                  disabled={saving}
                  onClick={saveAssignees}
                >
                  Apply
                </button>
              </div>

              <div className={styles.detailDivider} />
              <h4 className={styles.detailSectionTitle}>Attachments</h4>
              <input
                ref={fileInputRef}
                type="file"
                hidden
                onChange={onPickAttachment}
              />
              <button
                type="button"
                className={styles.btnSecondary}
                style={{ marginBottom: 8 }}
                onClick={() => fileInputRef.current?.click()}
              >
                Upload file
              </button>
              <ul style={{ paddingLeft: 18, fontSize: 14 }}>
                {attachments.map((a) => (
                  <li key={a.id} style={{ marginBottom: 6 }}>
                    <button
                      type="button"
                      style={{
                        background: "none",
                        border: "none",
                        color: "#1565c0",
                        cursor: "pointer",
                        textDecoration: "underline",
                      }}
                      onClick={() => downloadAtt(a)}
                    >
                      {a.filename}
                    </button>
                    <button
                      type="button"
                      style={{ marginLeft: 8, fontSize: 12 }}
                      onClick={() => removeAtt(a.id)}
                    >
                      Remove
                    </button>
                  </li>
                ))}
              </ul>

              <div className={styles.detailDivider} />
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <h4 className={styles.detailSectionTitle} style={{ margin: 0 }}>
                  Activity (audit)
                </h4>
                <button
                  type="button"
                  className={styles.btnSecondary}
                  onClick={refreshAudit}
                >
                  Refresh
                </button>
              </div>
              <ul
                style={{
                  paddingLeft: 18,
                  fontSize: 12,
                  maxHeight: 160,
                  overflow: "auto",
                }}
              >
                {auditRows.map((r) => (
                  <li key={r.id}>
                    {formatDate(r.created_at)} — {r.action}{" "}
                    {r.details ? JSON.stringify(r.details) : ""}
                  </li>
                ))}
              </ul>

              <div className={styles.detailDivider} />

              <h4 className={styles.detailSectionTitle}>
                Comments ({selectedIssue.comments.length})
              </h4>

              <div className={styles.commentList}>
                {selectedIssue.comments.map((c) => (
                  <div key={c.id} className={styles.comment}>
                    <span className={styles.commentAvatar}>
                      {getInitials(c.author)}
                    </span>
                    <div className={styles.commentBody}>
                      <span className={styles.commentAuthor}>{c.author}</span>
                      <span className={styles.commentTime}>{c.time}</span>
                      <p className={styles.commentText}>{c.text}</p>
                      {currentUserId != null &&
                        c.author_id === currentUserId && (
                          <button
                            type="button"
                            style={{ fontSize: 12 }}
                            onClick={() => removeComment(c.id)}
                          >
                            Delete
                          </button>
                        )}
                    </div>
                  </div>
                ))}

                {selectedIssue.comments.length === 0 && (
                  <p className={styles.detailDescription}>No comments yet.</p>
                )}
              </div>

              <div className={styles.commentInput}>
                <input
                  className={styles.commentInputField}
                  placeholder="Add a comment..."
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") sendComment();
                  }}
                />
                <button
                  type="button"
                  className={styles.sendBtn}
                  disabled={saving || !commentText.trim()}
                  onClick={sendComment}
                >
                  <Send size={14} />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Issues;
